import { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Typography,
} from "@mui/material";
import Brightness1Icon from "@mui/icons-material/Brightness1";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CustomAxios from "../utils/CustomAxios";
import main from "../assets/scss/main.module.scss";

import { CustomerRallyDetailData, RallyData, RewardData } from "../type";
import { Title } from "../components/common/Title";
import LoyaltyIcon from "@mui/icons-material/Loyalty";
import { ChangeModal } from "../components/reward/ChangeModal";
import { useNoticeContext } from "../context/NoticeContext";
const stampMax = 10;

export const RallyDetail = () => {
  const location = useLocation();
  const { rallyId } = useParams();
  const navigate = useNavigate();
  const { notice, setNotice, severity } = useNoticeContext();

  const issuerId = location?.state?.issuerId;

  const [isRallyDetailLoading, setIsRallyDetailLoading] = useState(false);
  const [isRewardLoading, setIsRewardLoading] = useState(false);
  const [customerRallyDetailData, setCustomerRallyDetailData] =
    useState<CustomerRallyDetailData>();
  const [rallyDetailData, setRallyDetailData] = useState<RallyData>();

  const [selectReward, setSelectReward] = useState<RewardData>();
  const [isPointSpendModalOpen, setIsPointSpendModalOpen] =
    useState<boolean>(false);

  useEffect(() => {
    setIsRallyDetailLoading(true);
    CustomAxios.get(`/customer/rally?rallyId=${rallyId}`).then((res) => {
      setCustomerRallyDetailData(res.data[`${rallyId}`]);
      setIsRallyDetailLoading(false);
    });

    CustomAxios.get(`/rally/${rallyId}`)
      .then((res) => {
        setRallyDetailData(res.data);
        setIsRewardLoading(false);
      })
      .catch(() => {
        navigate("/");
      });
  }, []);

  const editDateFormat = (dateStr?: string): string => {
    if (dateStr) {
      const date = new Date(dateStr);
      return date.toLocaleDateString("ja-JP");
    } else {
      return "";
    }
  };

  const handleReward = (item: RewardData) => {
    setIsPointSpendModalOpen(true);
    setSelectReward(item);
  };

  return (
    <>
      <Title
        name={rallyDetailData?.name}
        type={issuerId ? `map/${issuerId}` : "rally"}
      />
      {issuerId}
      {isRallyDetailLoading ? (
        <CircularProgress />
      ) : (
        <>
          <Paper
            elevation={0}
            sx={{
              backgroundColor: main.colorLightGreen,
              py: 2,
              mt: "70px",
              mb: 2,
            }}
          >
            <Paper
              elevation={1}
              sx={{ width: "90%", mx: "auto", p: 2, borderRadius: "16px" }}
            >
              <Typography>保有ラリーポイント</Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "baseline",
                  justifyContent: "center",
                  mt: 1,
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                  {customerRallyDetailData?.stamp
                    ? customerRallyDetailData?.stamp.toLocaleString()
                    : 0}
                </Typography>
                <Typography fontSize="14px" sx={{ ml: 1 }}>
                  pt
                </Typography>
              </Box>
            </Paper>
          </Paper>

          {notice && (
            <Alert
              severity={severity}
              onClose={() => setNotice(null)}
              sx={{ width: "70%", mx: "auto", my: "30px" }}
            >
              {notice}
            </Alert>
          )}

          {rallyDetailData?.startDate && rallyDetailData?.endDate && (
            <Typography>
              開催期間：{editDateFormat(rallyDetailData?.startDate)} -{" "}
              {editDateFormat(rallyDetailData?.endDate)}
            </Typography>
          )}
          <Typography sx={{ mt: 2, mb: 4 }}>
            {rallyDetailData?.description}
          </Typography>
          <Divider sx={{ mt: 2, mb: 4, width: "90%", mx: "auto" }} />

          {/* 
            <Container　// スタンプカード
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(5,50px)",
                justifyContent: "center",
                justifyItems: "center",
                gap: "10px 10px",
              }}
            >
              {[...Array(rallyDetailData?.stamp)].map(() => (
                <CheckCircleIcon fontSize="large" color="primary" />
              ))}
              {[...Array(stampMax - (rallyDetailData?.stamp ?? 0))].map(() => (
                <Brightness1Icon fontSize="large" sx={{ color: "gray" }} />
              ))}
            </Container> */}

          <Typography variant="h6" sx={{ mt: 1, mb: 2 }}>
            交換可能景品
          </Typography>
          {isRewardLoading && <CircularProgress />}
          {!isRewardLoading &&
            rallyDetailData?.rewards.map((item, index) => (
              <Paper
                key={index}
                onClick={() => handleReward(item)}
                sx={{
                  m: 2,
                  px: 1,
                  py: 2,
                  textAlign: "left",
                  cursor: "pointer",
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    m: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    <LoyaltyIcon
                      fontSize="large"
                      sx={{ mr: 2, color: main.colorDarkGreen }}
                    />
                    <Box>
                      <Typography sx={{ fontSize: "20px" }}>
                        {item.name}
                      </Typography>
                      <Typography sx={{ fontSize: "14px" }}>
                        {item.issuerName}
                      </Typography>
                    </Box>
                  </Box>
                  <Paper
                    elevation={0}
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{ display: "inline", fontWeight: "bold", mx: 1 }}
                    >
                      {item.point}
                    </Typography>
                    pt
                  </Paper>
                </Paper>
                <Paper elevation={0} sx={{ textAlign: "right" }}>
                  {(customerRallyDetailData?.stamp ?? 0) < item.point ? (
                    <Chip
                      label={"ポイントが足りません"}
                      color="error"
                      size="small"
                      sx={{ color: "#FFF", ml: 4, px: 2 }}
                    />
                  ) : (
                    ""
                  )}
                </Paper>
              </Paper>
            ))}

          <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
            参加店舗
          </Typography>
          <Box>
            {rallyDetailData?.issuers.map((item, index) => (
              <Paper
                key={index}
                sx={{ p: 2, m: 2 }}
                onClick={() => navigate(`/map/${item.uuid}`)}
              >
                <Typography>{item.name}</Typography>
              </Paper>
            ))}
          </Box>

          <ChangeModal
            isPointSpendModalOpen={isPointSpendModalOpen}
            setIsPointSpendModalOpen={setIsPointSpendModalOpen}
            selectReward={selectReward}
            rallyPoint={customerRallyDetailData?.stamp ?? 0}
          />
        </>
      )}
    </>
  );
};
